/**
 * Typisierte Hooks für `/api/v1/contracts` (Spec 03, `domains/contracts`),
 * dazu eine schmale Mieterauswahl gegen `/api/v1/tenants` für das Sheet
 * „Vertrag anlegen" (`contract-tenant-search`).
 *
 * `fetch` steht ausschließlich hier — nie in einer Komponente
 * (`apps/web/CLAUDE.md`). Query-Keys kommen ausschließlich aus
 * `lib/query-keys.ts`; jede Mutation invalidiert über ein Präfix.
 *
 * Die Mieterauswahl gehört fachlich zu `domains/tenants`, liegt aber hier:
 * `features/contracts` kennt `features/tenants` nicht (`apps/web/CLAUDE.md`,
 * „features kennen einander nicht") — derselbe Schnitt wie `useArchivePreview`
 * in `features/properties/api.ts` (Spec 03, „Was 03 an Bestehendem ändert",
 * Punkt 7): ein eigener, kleiner `request()`-Aufruf statt eines verbotenen
 * Imports. Der Query-Key liegt trotzdem unter dem Präfix `tenants`
 * (`keys.tenants.search`), damit `useCreateTenant`/`useUpdateTenant" in
 * `features/tenants/api.ts` ihn mit ihrer eigenen `tenants.all`-Invalidierung
 * mittreffen, ohne von diesem Modul zu wissen.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { components } from '@/lib/api-types'
import { request } from '@/lib/api'
import { keys } from '@/lib/query-keys'

export type ContractListItem = components['schemas']['ContractListItem']
export type ContractDetail = components['schemas']['ContractDetail']
export type ContractCreate = components['schemas']['ContractCreate']
export type ContractUpdate = components['schemas']['ContractUpdate']
export type ContractTermSummary = components['schemas']['ContractTermSummary']
export type ContractTermsCreate = components['schemas']['ContractTermsCreate']
export type TermReason = components['schemas']['TermReason']
export type LabeledValue = components['schemas']['LabeledValue']

/** Ein Treffer der Mieterauswahl — dieselbe Form wie `TenantListItem` in
 * `features/tenants/api.ts`, hier noch einmal lokal (siehe Kopf dieser
 * Datei). */
export type TenantOption = components['schemas']['TenantListItem']

/** Antwort von `GET /entities/{id}/archive-preview` — dieselbe wie in
 * `features/properties/api.ts` und `features/tenants/api.ts`. */
export type ArchivePreview = components['schemas']['ArchivePreview']

interface ContractsPage {
  readonly items: ContractListItem[]
  readonly next_cursor: string | null
}

interface QueryOptions {
  readonly enabled?: boolean
}

export interface ContractsListFilter {
  readonly subjectId?: string
  readonly tenantId?: string
  readonly status?: string
}

/**
 * `GET /contracts` — mit `?subject_id=` zeigt sie den Teilbaum eines
 * Objekts (Reiter *Geld* an Grundstück, Gebäude, Einheit), mit `?tenant_id=`
 * die Verträge eines Mieters (Reiter *Geld* am Mieter). Genau eines der
 * beiden ist gesetzt — nie beide, nie keines (Spec 03, „Oberfläche · Reiter
 * Geld"), deshalb kein `?status=` hier: 03 zeigt jeden Vertrag, filtert ihn
 * aber nicht (das ist Feature 04, „Klärfälle").
 */
export function useContractsList(filter: ContractsListFilter, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.contracts.list({ subjectId: filter.subjectId, tenantId: filter.tenantId, status: filter.status }),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams()
      if (filter.subjectId !== undefined) params.set('subject_id', filter.subjectId)
      if (filter.tenantId !== undefined) params.set('tenant_id', filter.tenantId)
      if (filter.status !== undefined) params.set('status', filter.status)
      const query = params.toString() === '' ? '' : `?${params.toString()}`
      return request<ContractsPage>(`/contracts${query}`, { signal })
    },
    enabled: options.enabled ?? true,
  })
}

/** Cursorfortsetzung der Vertragsbeziehung in der Objektübersicht. Der
 * Filter bleibt vollständig im Key; eine neue Objektansicht übernimmt daher
 * weder Cursor noch verspätete Seiten der vorherigen Beziehung. */
export function useInfiniteContractsList(filter: ContractsListFilter, options: QueryOptions = {}) {
  return useInfiniteQuery({
    queryKey: keys.contracts.list({ subjectId: filter.subjectId, tenantId: filter.tenantId, status: filter.status }),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams()
      if (filter.subjectId !== undefined) params.set('subject_id', filter.subjectId)
      if (filter.tenantId !== undefined) params.set('tenant_id', filter.tenantId)
      if (filter.status !== undefined) params.set('status', filter.status)
      if (pageParam !== null) params.set('cursor', pageParam)
      const query = params.toString() === '' ? '' : `?${params.toString()}`
      return request<ContractsPage>(`/contracts${query}`, { signal })
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled: options.enabled ?? true,
  })
}

export function useContractDetail(contractId: string, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.contracts.detail(contractId),
    queryFn: ({ signal }) => request<ContractDetail>(`/contracts/${contractId}`, { signal }),
    enabled: options.enabled ?? true,
  })
}

interface CreateContractArgs {
  readonly body: ContractCreate
}

export function useCreateContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ body }: CreateContractArgs) =>
      request<ContractDetail>('/contracts', {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      // Kein `entities.all`: Ein Vertrag steht nie im Baum, und `has_children`
      // seines Vertragsobjekts ändert sich dadurch nicht (Spec 03, AK-179) —
      // die einzige betroffene Domäne ist die eigene Vertragsliste.
      void queryClient.invalidateQueries({ queryKey: keys.contracts.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface UpdateContractArgs {
  readonly contractId: string
  readonly body: ContractUpdate
  readonly ifMatch: string
}

export function useUpdateContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ contractId, body, ifMatch }: UpdateContractArgs) =>
      request<ContractDetail>(`/contracts/${contractId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      // `entities.all` zusätzlich: Der Service schreibt `entities.label` bei
      // jedem `PATCH` fort, der `contract_ref` oder `starts_on` ändert
      // (Spec 03, „Der Name eines Vertrags") — ohne diese Invalidierung zeigte
      // der Kopf der Objektansicht (`contract-label`) den alten Titel.
      void queryClient.invalidateQueries({ queryKey: keys.contracts.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface ArchiveContractArgs {
  readonly contractId: string
  readonly ifMatch: string
}

export function useArchiveContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ contractId, ifMatch }: ArchiveContractArgs) =>
      request<ContractDetail>(`/contracts/${contractId}/archive`, { method: 'POST', ifMatch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.contracts.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

export function useUnarchiveContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ contractId, ifMatch }: ArchiveContractArgs) =>
      request<ContractDetail>(`/contracts/${contractId}/unarchive`, { method: 'POST', ifMatch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.contracts.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

/** `GET /entities/{id}/archive-preview` — als Mutation statt `useQuery`, wie
 * in `features/properties/api.ts`. */
export function useArchivePreview() {
  return useMutation({
    mutationFn: (entityId: string) => request<ArchivePreview>(`/entities/${entityId}/archive-preview`),
  })
}

/** `GET /contracts/term-reasons` — die Gründe im Sheet „Konditionen ändern"
 * (`contract-terms-reason`, Spec 04a). Ändert sich nie zur Laufzeit, deshalb
 * ein fester Key ohne Filter, wie `units.usageTypes`. */
export function useTermReasons() {
  return useQuery({
    queryKey: keys.contracts.termReasons,
    queryFn: ({ signal }) => request<LabeledValue[]>('/contracts/term-reasons', { signal }),
  })
}

interface AddContractTermsArgs {
  readonly contractId: string
  readonly body: ContractTermsCreate
}

/**
 * `POST /contracts/{id}/terms` — der Konditionswechsel (Spec 04a, „Sheet
 * Konditionen ändern"). Kein `If-Match`: Die Datenbank hat mit
 * `contract_terms_open_idx` bereits das Netz gegen zwei gleichzeitige
 * Wechsel, die Spec verlangt hier keine Version.
 *
 * Invalidiert zusätzlich `keys.billing.all`: Ein rückwirkender Satz erzeugt
 * fehlende Sollstellungen (`charges_created`), die in `open-items`,
 * `summary` und den Sollstellungen dieses Vertrags auftauchen — derselbe
 * Grund, aus dem `features/billing/api.ts` seinerseits nie
 * `features/contracts` importiert: Der gemeinsame Key kommt aus `lib/`, kein
 * verbotener Import zwischen zwei `features/*`.
 */
export function useAddContractTerms() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ contractId, body }: AddContractTermsArgs) =>
      request<ContractDetail>(`/contracts/${contractId}/terms`, {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.contracts.all })
      void queryClient.invalidateQueries({ queryKey: keys.billing.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface TenantOptionsPage {
  readonly items: TenantOption[]
  readonly next_cursor: string | null
}

/**
 * `GET /tenants?q=` — die Mieterauswahl im Vertragssheet
 * (`contract-tenant-search`/`contract-tenant-option-<id>`, Spec 03, „Sheet
 * Vertrag anlegen"). Ohne Suchtext leer, damit nicht beim Öffnen des Sheets
 * sofort alle Mieter geladen werden — der Nutzer tippt, wie bei
 * `MoveSheet`s Zielsuche.
 */
export function useTenantOptions(q: string, options: QueryOptions = {}) {
  const trimmed = q.trim()

  return useQuery({
    queryKey: keys.tenants.search(trimmed),
    queryFn: ({ signal }) => request<TenantOptionsPage>(`/tenants?q=${encodeURIComponent(trimmed)}`, { signal }),
    enabled: (options.enabled ?? true) && trimmed !== '',
  })
}
